import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  RocketLaunchIcon,
  SparklesIcon,
  ChartBarIcon,
  CpuChipIcon,
  PlusIcon,
  PlayIcon,
  EyeIcon
} from '@heroicons/react/24/outline';

// Import components
import ManageDashboards from './ManageDashboards';
import EditDashboards from './EditDashboards';
import ModernDashboard from './ModernDashboard';
import DashboardAnalytics from './DashboardAnalytics';
import DashboardPerformance from './DashboardPerformance';
import WidgetLibrary from './widgets/WidgetLibrary';

const ShowcaseContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, ${theme.colors.background.primary} 0%, ${theme.colors.background.secondary} 100%);
  padding: 2rem;
`;

const ShowcaseHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const ShowcaseTitle = styled.h1`
  color: ${theme.colors.text.primary};
  font-size: 3rem;
  font-weight: ${theme.fontWeights.bold};
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
`;

const ShowcaseSubtitle = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.lg};
  margin: 0 0 2rem 0;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
`;

const FeatureCard = styled.div`
  background: ${theme.colors.background.paper};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  padding: 2rem;
  text-align: center;
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: ${theme.shadows.lg};
    border-color: ${theme.colors.primary.main};
  }
`;

const FeatureIcon = styled.div`
  width: 4rem;
  height: 4rem;
  background: ${theme.colors.primary.light};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem auto;
  color: ${theme.colors.primary.main};
`;

const FeatureTitle = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.xl};
  font-weight: ${theme.fontWeights.semibold};
  margin: 0 0 1rem 0;
`;

const FeatureDescription = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.md};
  line-height: 1.6;
  margin: 0 0 1.5rem 0;
`;

const FeatureButton = styled.button`
  background: ${theme.colors.primary.main};
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.fontSizes.md};
  font-weight: ${theme.fontWeights.medium};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0 auto;
  
  &:hover {
    background: ${theme.colors.primary.dark};
  }
`;

const DemoSection = styled.div`
  background: ${theme.colors.background.paper};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  padding: 2rem;
  margin-bottom: 2rem;
`;

const DemoHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const DemoTitle = styled.h2`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.h2};
  font-weight: ${theme.fontWeights.semibold};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const TabContainer = styled.div`
  display: flex;
  background: ${theme.colors.background.secondary};
  border-radius: ${theme.borderRadius.md};
  padding: 0.25rem;
  margin-bottom: 2rem;
`;

const Tab = styled.button`
  flex: 1;
  padding: 0.75rem 1rem;
  border: none;
  background: ${props => props.active ? theme.colors.primary.main : 'transparent'};
  color: ${props => props.active ? 'white' : theme.colors.text.primary};
  border-radius: ${theme.borderRadius.sm};
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.medium};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.active ? theme.colors.primary.dark : theme.colors.background.primary};
  }
`;

const StatsBar = styled.div`
  display: flex;
  justify-content: center;
  gap: 3rem;
  margin: 3rem 0;
  padding: 2rem;
  background: ${theme.colors.background.paper};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.border.primary};
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 2.5rem;
  font-weight: ${theme.fontWeights.bold};
  color: ${theme.colors.primary.main};
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const DashboardShowcase = () => {
  const navigate = useNavigate();
  const [activeDemo, setActiveDemo] = useState('manager');
  const [showWizard, setShowWizard] = useState(false);
  const [demoStats, setDemoStats] = useState({
    dashboards: 0,
    dataPoints: 0,
    widgets: 0,
    users: 0
  });

  useEffect(() => {
    // Animate stats on load
    const animateStats = () => {
      const targets = { dashboards: 47, dataPoints: 119631, widgets: 24, users: 156 };
      const duration = 2000;
      const steps = 60;
      const stepDuration = duration / steps;
      
      let step = 0;
      const interval = setInterval(() => {
        step++;
        const progress = step / steps;
        
        setDemoStats({
          dashboards: Math.floor(targets.dashboards * progress),
          dataPoints: Math.floor(targets.dataPoints * progress),
          widgets: Math.floor(targets.widgets * progress),
          users: Math.floor(targets.users * progress)
        });
        
        if (step >= steps) {
          clearInterval(interval);
        }
      }, stepDuration);
    };
    
    animateStats();
  }, []);

  const features = [
    {
      icon: <ChartBarIcon className="w-8 h-8" />,
      title: 'Dashboard Manager',
      description: 'Create, organize, and manage multiple dashboards with an intuitive interface. Search, filter, and organize your vessel monitoring dashboards.',
      action: () => setActiveDemo('manager'),
      buttonText: 'View Manager'
    },
    {
      icon: <SparklesIcon className="w-8 h-8" />,
      title: 'Creation Wizard',
      description: 'Step-by-step dashboard creation with templates, vessel selection, and card configuration. Build professional dashboards in minutes.',
      action: () => setShowWizard(true),
      buttonText: 'Try Wizard'
    },
    {
      icon: <RocketLaunchIcon className="w-8 h-8" />,
      title: 'Real-time Dashboard',
      description: 'Live data visualization with modern card-based interface. Monitor electrical, navigation, engine, and environmental systems.',
      action: () => setActiveDemo('dashboard'),
      buttonText: 'View Dashboard'
    },
    {
      icon: <ChartBarIcon className="w-8 h-8" />,
      title: 'Analytics & Insights',
      description: 'Comprehensive analytics including usage statistics, performance metrics, and user engagement data.',
      action: () => setActiveDemo('analytics'),
      buttonText: 'View Analytics'
    },
    {
      icon: <CpuChipIcon className="w-8 h-8" />,
      title: 'Performance Monitor',
      description: 'Real-time system performance monitoring with load times, data latency, and health alerts.',
      action: () => setActiveDemo('performance'),
      buttonText: 'View Monitor'
    },
    {
      icon: <PlusIcon className="w-8 h-8" />,
      title: 'Widget Library',
      description: 'Extensive library of pre-built widgets for navigation, electrical, engine, and environmental monitoring.',
      action: () => setActiveDemo('widgets'),
      buttonText: 'Browse Widgets'
    }
  ];

  const handleCreateDashboard = () => {
    toast.success('Dashboard creation started!');
    navigate('/dashboards/new');
  };

  // const handleViewDashboards = () => {
  //   navigate('/dashboards');
  // };

  const renderDemo = () => {
    switch (activeDemo) {
      case 'manager':
        return <ManageDashboards />;
      case 'dashboard':
        return <ModernDashboard />;
      case 'analytics':
        return <DashboardAnalytics dashboardId="demo" />;
      case 'performance':
        return <DashboardPerformance dashboardId="demo" />;
      case 'widgets':
        return <WidgetLibrary onAddWidget={(widget) => toast.success(`Added ${widget.title} widget!`)} />;
      default:
        return <ManageDashboards />;
    }
  };

  return (
    <ShowcaseContainer>
      <ShowcaseHeader>
        <ShowcaseTitle>
          <RocketLaunchIcon className="w-12 h-12" />
          BoatWise Dashboard System
        </ShowcaseTitle>
        <ShowcaseSubtitle>
          Professional marine dashboard platform with real-time data visualization, 
          comprehensive analytics, and advanced monitoring capabilities for modern vessels.
        </ShowcaseSubtitle>
      </ShowcaseHeader>

      <StatsBar>
        <StatItem>
          <StatValue>{demoStats.dashboards}</StatValue>
          <StatLabel>Active Dashboards</StatLabel>
        </StatItem>
        <StatItem>
          <StatValue>{demoStats.dataPoints.toLocaleString()}</StatValue>
          <StatLabel>Data Points</StatLabel>
        </StatItem>
        <StatItem>
          <StatValue>{demoStats.widgets}</StatValue>
          <StatLabel>Widget Types</StatLabel>
        </StatItem>
        <StatItem>
          <StatValue>{demoStats.users}</StatValue>
          <StatLabel>Active Users</StatLabel>
        </StatItem>
      </StatsBar>

      <FeatureGrid>
        {features.map((feature, index) => (
          <FeatureCard key={index} onClick={feature.action}>
            <FeatureIcon>{feature.icon}</FeatureIcon>
            <FeatureTitle>{feature.title}</FeatureTitle>
            <FeatureDescription>{feature.description}</FeatureDescription>
            <FeatureButton>
              <EyeIcon className="w-4 h-4" />
              {feature.buttonText}
            </FeatureButton>
          </FeatureCard>
        ))}
      </FeatureGrid>

      <DemoSection>
        <DemoHeader>
          <DemoTitle>
            <PlayIcon className="w-6 h-6" />
            Live Demo
          </DemoTitle>
        </DemoHeader>

        <TabContainer>
          <Tab active={activeDemo === 'manager'} onClick={() => setActiveDemo('manager')}>
            Dashboard Manager
          </Tab>
          <Tab active={activeDemo === 'dashboard'} onClick={() => setActiveDemo('dashboard')}>
            Live Dashboard
          </Tab>
          <Tab active={activeDemo === 'analytics'} onClick={() => setActiveDemo('analytics')}>
            Analytics
          </Tab>
          <Tab active={activeDemo === 'performance'} onClick={() => setActiveDemo('performance')}>
            Performance
          </Tab>
          <Tab active={activeDemo === 'widgets'} onClick={() => setActiveDemo('widgets')}>
            Widget Library
          </Tab>
        </TabContainer>

        {renderDemo()}
      </DemoSection>

      {showWizard && (
        <EditDashboards 
          onClose={() => setShowWizard(false)}
          onComplete={handleCreateDashboard}
        />
      )}
    </ShowcaseContainer>
  );
};

export default DashboardShowcase;